import React from 'react';
import { Box, Typography } from '@mui/material';
import { marketingPalette } from '../marketingSystem';

/**
 * Two scales of the same optimization.
 *
 * Per-intent (micro): every payment is scored against the priority weights
 * and cleared through the composition that best serves the buyer's context.
 *
 * Across the book (macro): the same optimization respects portfolio-level
 * commercial pressures — tier thresholds, volume commitments, counterparty
 * concentration, blended cost curves — so short-term routing choices stay
 * consistent with long-term commercial posture.
 *
 * Kept universal: no method names, no counterparty names. The vocabulary is
 * the same one used on the triangle and in the hero.
 */

type ScalePanel = {
  id: 'micro' | 'macro';
  eyebrow: string;
  title: string;
  summary: string;
  bullets: Array<{ label: string; body: string }>;
  accent: string;
  accentSoft: string;
  accentBorder: string;
};

const panels: ScalePanel[] = [
  {
    id: 'micro',
    eyebrow: 'Per intent',
    title: 'Every transaction, optimized against the weights.',
    summary:
      'On each intent, score every candidate path against the priority weights and clear through the composition that serves that buyer best.',
    bullets: [
      {
        label: 'Score in real time',
        body: 'Every candidate route gets an expected-value score against the priorities you set — not the same answer for every buyer.',
      },
      {
        label: 'Match the context',
        body: 'Route selection reflects the buyer segment, basket shape, and risk signals in front of you — not a global default.',
      },
      {
        label: 'Clear with the posture',
        body: 'The composition that reaches the buyer is the one the current posture says wins this specific tradeoff.',
      },
    ],
    accent: '#0f6fff',
    accentSoft: 'rgba(15,111,255,0.06)',
    accentBorder: 'rgba(15,111,255,0.28)',
  },
  {
    id: 'macro',
    eyebrow: 'Across the book',
    title: 'The portfolio shape optimized alongside the individual route.',
    summary:
      'The same optimization keeps the stream of per-intent decisions aligned with the commercial pressures that only show up at scale.',
    bullets: [
      {
        label: 'Tier pricing thresholds',
        body: 'Steer volume toward the next pricing step when the gap is close — a sliver of shifted traffic re-prices the stream behind it.',
      },
      {
        label: 'Volume commitments',
        body: 'Hit monthly minimums and commitment floors without starving the routes that are performing for buyers.',
      },
      {
        label: 'Counterparty concentration',
        body: 'Manage the share of the book landing on each counterparty — leverage for the next negotiation, resilience for today.',
      },
      {
        label: 'Blended cost stability',
        body: 'Smooth the book-level cost curve across the billing period so finance gets a shape, not a series of surprises.',
      },
    ],
    accent: '#7c3aed',
    accentSoft: 'rgba(124,58,237,0.06)',
    accentBorder: 'rgba(124,58,237,0.28)',
  },
];

const ScalesOfOptimization: React.FC = () => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: { xs: 2.5, md: 3 },
        alignItems: 'stretch',
      }}
    >
      {panels.map((panel) => (
        <Box
          key={panel.id}
          sx={{
            position: 'relative',
            p: { xs: 2.2, md: 2.8 },
            borderRadius: 1.25,
            border: `1px solid ${marketingPalette.border}`,
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.985) 0%, rgba(250,252,255,0.985) 100%)',
            boxShadow: '0 12px 28px rgba(15,23,42,0.045)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.6,
            overflow: 'hidden',
          }}
        >
          {/* Corner accent glow */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: -70,
              right: -70,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: `radial-gradient(closest-side, ${panel.accent}1f, transparent 70%)`,
              pointerEvents: 'none',
            }}
          />

          {/* Header */}
          <Box sx={{ position: 'relative' }}>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                px: 0.9,
                py: 0.45,
                borderRadius: 0.7,
                backgroundColor: panel.accentSoft,
                border: `1px solid ${panel.accentBorder}`,
                color: panel.accent,
                fontSize: '0.62rem',
                fontWeight: 400,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                lineHeight: 1,
              }}
            >
              {panel.eyebrow}
            </Box>
            <Typography
              sx={{
                mt: 1,
                color: marketingPalette.headline,
                fontWeight: 400,
                fontSize: { xs: '1.05rem', md: '1.18rem' },
                lineHeight: 1.3,
                letterSpacing: '-0.015em',
              }}
            >
              {panel.title}
            </Typography>
            <Typography
              sx={{
                mt: 0.8,
                color: marketingPalette.body,
                fontSize: '0.88rem',
                lineHeight: 1.58,
                fontWeight: 400,
              }}
            >
              {panel.summary}
            </Typography>
          </Box>

          {/* Mini visualization */}
          {panel.id === 'micro' ? (
            <Box
              sx={{
                p: 1.2,
                borderRadius: 1,
                border: `1px solid ${panel.accentBorder}`,
                backgroundColor: panel.accentSoft,
                mb: 0.5,
              }}
            >
              <Typography sx={{ color: panel.accent, fontSize: '0.58rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.85 }}>
                Route scoring · single intent
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.55 }}>
                {[
                  { label: 'Route A · wallet path', score: 0.84, selected: true },
                  { label: 'Route B · card tokenized', score: 0.61, selected: false },
                  { label: 'Route C · direct card', score: 0.44, selected: false },
                ].map((route) => (
                  <Box key={route.label} sx={{ display: 'grid', gridTemplateColumns: '1fr 36px', gap: 1, alignItems: 'center' }}>
                    <Box sx={{ position: 'relative', height: 12, borderRadius: 999, backgroundColor: 'rgba(196,212,232,0.35)', overflow: 'hidden' }}>
                      <Box sx={{
                        position: 'absolute', left: 0, top: 0, bottom: 0,
                        width: `${route.score * 100}%`,
                        background: route.selected
                          ? `linear-gradient(90deg, ${panel.accent} 0%, ${panel.accent}cc 100%)`
                          : 'linear-gradient(90deg, rgba(148,163,184,0.55) 0%, rgba(148,163,184,0.35) 100%)',
                        borderRadius: 999,
                        boxShadow: route.selected ? `0 0 10px ${panel.accent}55` : 'none',
                      }} />
                    </Box>
                    <Typography sx={{ color: route.selected ? panel.accent : marketingPalette.muted, fontFamily: 'monospace', fontSize: '0.64rem', fontWeight: 400, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {route.score.toFixed(2)}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: panel.accent, boxShadow: `0 0 6px ${panel.accent}88` }} />
                  <Typography sx={{ color: panel.accent, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.05em' }}>Route A selected · 38 ms</Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                p: 1.2,
                borderRadius: 1,
                border: `1px solid ${panel.accentBorder}`,
                backgroundColor: panel.accentSoft,
                mb: 0.5,
              }}
            >
              <Typography sx={{ color: panel.accent, fontSize: '0.58rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.85 }}>
                Portfolio metrics · current period
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
                {[
                  { label: 'Tier threshold', pct: 82, note: '3 pts from next tier', color: panel.accent },
                  { label: 'Volume commitment', pct: 91, note: 'On track', color: '#047857' },
                  { label: 'Counterparty conc.', pct: 62, note: 'Balanced', color: '#0f6fff' },
                ].map((metric) => (
                  <Box key={metric.label}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                      <Typography sx={{ color: marketingPalette.body, fontSize: '0.62rem', fontWeight: 400 }}>{metric.label}</Typography>
                      <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400 }}>{metric.note}</Typography>
                    </Box>
                    <Box sx={{ position: 'relative', height: 12, borderRadius: 999, backgroundColor: 'rgba(196,212,232,0.35)', overflow: 'hidden' }}>
                      <Box sx={{
                        position: 'absolute', left: 0, top: 0, bottom: 0,
                        width: `${metric.pct}%`,
                        background: `linear-gradient(90deg, ${metric.color} 0%, ${metric.color}cc 100%)`,
                        borderRadius: 999,
                        boxShadow: `0 0 8px ${metric.color}44`,
                      }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Bullets */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
            {panel.bullets.map((bullet) => (
              <Box
                key={bullet.label}
                sx={{
                  position: 'relative',
                  p: 1.1,
                  pl: 1.5,
                  borderRadius: 0.9,
                  border: `1px solid ${marketingPalette.border}`,
                  backgroundColor: '#ffffff',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: `linear-gradient(180deg, ${panel.accent} 0%, ${panel.accent}88 100%)`,
                  }}
                />
                <Typography
                  sx={{
                    color: marketingPalette.muted,
                    fontSize: '0.6rem',
                    fontWeight: 400,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    mb: 0.35,
                  }}
                >
                  {bullet.label}
                </Typography>
                <Typography
                  sx={{
                    color: marketingPalette.headline,
                    fontSize: '0.82rem',
                    fontWeight: 400,
                    lineHeight: 1.5,
                  }}
                >
                  {bullet.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ))}

      {/* Bridge caption beneath the two panels */}
      <Box
        sx={{
          gridColumn: { xs: '1', md: '1 / -1' },
          mt: { xs: 0.5, md: 1 },
          p: { xs: 1.6, md: 2 },
          borderRadius: 1.1,
          border: `1px solid ${marketingPalette.border}`,
          backgroundColor: '#fbfdff',
          display: 'flex',
          gap: 1.4,
          alignItems: 'flex-start',
        }}
      >
        <Box
          aria-hidden
          sx={{
            mt: 0.35,
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0f6fff 0%, #7c3aed 100%)',
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            color: marketingPalette.headline,
            fontSize: { xs: '0.86rem', md: '0.9rem' },
            fontWeight: 400,
            lineHeight: 1.58,
          }}
        >
          The layers don't run in isolation. A route that looks cheapest on a single intent can be worse at the book level —
          steering a sliver of volume to a different counterparty pushes the business through a tier threshold and re-prices
          the downstream stream. Ormal operates both scales against the same priority weights, so short-term decisions
          compound into long-term commercial posture instead of fighting it.
        </Typography>
      </Box>
    </Box>
  );
};

export default ScalesOfOptimization;
